import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Toaster, toast } from "sonner";
import MockupForm from "@/components/admin/MockupForm";
import ConvertMockupModal from "@/components/admin/ConvertMockupModal";
import MockupConversionPanel from "@/components/admin/MockupConversionPanel";
import GenerateDraftModal from "@/components/admin/GenerateDraftModal";
import { buildInstagramDM } from "@/lib/outreachTemplates";
import {
  authHeaders,
  getCurrentUser,
  handleUnauthorized,
} from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OUTREACH_STAGE_LABELS = {
  not_contacted: "Not Contacted",
  initial_contact: "Initial Contact",
  followup_1: "Follow-up 1",
  followup_2: "Follow-up 2",
  followup_3: "Follow-up 3",
  followup_4: "Follow-up 4",
  followup_5: "Follow-up 5",
  final_followup: "Final Follow-up",
  replied: "Replied",
  activated: "Activated",
};

function outreachStageLabel(stage) {
  return OUTREACH_STAGE_LABELS[stage] || "Not Contacted";
}

function restaurantStatusMarker(mockup, conversion = null) {
  if (
    conversion?.payment_status === "paid" &&
    conversion?.account_status === "active"
  ) {
    return {
      label: "Active",
      className:
        "border-emerald-400/35 bg-emerald-400/15 text-emerald-300",
    };
  }

  if (conversion) {
    return {
      label: "Converted",
      className:
        "border-cyan-400/35 bg-cyan-400/15 text-cyan-300",
    };
  }

  const stage = mockup?.outreach_stage || "not_contacted";
  const attempts = Number(mockup?.outreach_attempt_count || 0);

  if (stage === "replied") {
    return {
      label: "Replied",
      className:
        "border-violet-400/35 bg-violet-400/15 text-violet-300",
    };
  }

  if (stage === "activated") {
    return {
      label: "Activated",
      className:
        "border-emerald-400/35 bg-emerald-400/15 text-emerald-300",
    };
  }

  if (stage !== "not_contacted" || attempts > 0) {
    return {
      label: "In Progress",
      className:
        "border-blue-400/35 bg-blue-400/15 text-blue-300",
    };
  }

  return {
    label: "New",
    className:
      "border-zinc-500/40 bg-zinc-500/10 text-zinc-300",
  };
}

function RestaurantStatusBadge({ mockup, conversion = null }) {
  const marker = restaurantStatusMarker(mockup, conversion);

  return (
    <span
      className={`inline-flex shrink-0 rounded-full border px-2 py-1 text-[10px] font-bold uppercase tracking-wide ${marker.className}`}
      title={`Restaurant status: ${marker.label}`}
    >
      {marker.label}
    </span>
  );
}

function inboundLeadSourceFields(value = "") {
  const raw = String(value || "").trim();

  if (!raw) {
    return {
      instagram_handle: "",
      website_url: "",
      source_note: "",
    };
  }

  const plainHandle = raw.replace(/^@/, "");

  if (/^[A-Za-z0-9._]{1,30}$/.test(plainHandle)) {
    return {
      instagram_handle: plainHandle,
      website_url: "",
      source_note: "",
    };
  }

  const urlCandidate =
    /^https?:\/\//i.test(raw)
      ? raw
      : /^www\./i.test(raw)
        ? `https://${raw}`
        : "";

  if (!urlCandidate) {
    return {
      instagram_handle: "",
      website_url: "",
      source_note: raw,
    };
  }

  try {
    const url = new URL(urlCandidate);
    const hostname = url.hostname
      .replace(/^www\./i, "")
      .toLowerCase();

    if (
      hostname === "instagram.com" ||
      hostname.endsWith(".instagram.com")
    ) {
      const handle =
        url.pathname
          .split("/")
          .filter(Boolean)[0]
          ?.replace(/^@/, "") || "";

      return {
        instagram_handle: /^[A-Za-z0-9._]{1,30}$/.test(handle)
          ? handle
          : "",
        website_url: "",
        source_note: handle ? "" : raw,
      };
    }

    const referenceOnlyHosts = [
      "facebook.com",
      "m.facebook.com",
      "tiktok.com",
      "x.com",
      "twitter.com",
      "google.com",
      "maps.google.com",
      "maps.app.goo.gl",
      "goo.gl",
      "g.page",
    ];

    if (
      referenceOnlyHosts.some(
        (host) =>
          hostname === host ||
          hostname.endsWith(`.${host}`)
      )
    ) {
      return {
        instagram_handle: "",
        website_url: "",
        source_note: raw,
      };
    }

    return {
      instagram_handle: "",
      website_url: urlCandidate,
      source_note: "",
    };
  } catch {
    return {
      instagram_handle: "",
      website_url: "",
      source_note: raw,
    };
  }
}

function initialValuesFromInboundLead(
  lead,
  staffMode,
  currentUser
) {
  if (!lead) return null;

  const source = inboundLeadSourceFields(
    lead.instagram_handle
  );

  const campaignType =
    lead.campaign_type === "event"
      ? "event"
      : "offer";

  const noteParts = [
    "Created from inbound TapToTable signup",
    lead.email ? `Customer email: ${lead.email}` : "",
    source.source_note
      ? `Signup source: ${source.source_note}`
      : "",
  ].filter(Boolean);

  return {
    source_lead_id: lead.id || "",
    restaurant_name: lead.restaurant_name || "",
    instagram_handle: source.instagram_handle,
    website_url: source.website_url,
    menu_url: lead.menu_url || "",
    hero_image_url: lead.image_url || "",
    campaign_type: campaignType,
    offer:
      campaignType === "offer"
        ? lead.offer || ""
        : "",
    event_name:
      campaignType === "event"
        ? lead.event_name || ""
        : "",
    event_date:
      campaignType === "event"
        ? lead.event_date || ""
        : "",
    event_time:
      campaignType === "event"
        ? lead.event_time || ""
        : "",
    event_description:
      campaignType === "event"
        ? lead.event_description || ""
        : "",
    event_action_text:
      campaignType === "event"
        ? lead.event_action_text ||
          "View Event Details"
        : "View Event Details",
    assigned_to:
      staffMode && currentUser?.id
        ? currentUser.id
        : lead.assigned_to || "owner",
    owner_id:
      staffMode && currentUser?.id
        ? currentUser.id
        : lead.owner_id || "owner",
    owner_email:
      staffMode && currentUser?.email
        ? currentUser.email
        : lead.owner_email || "",
    notes: noteParts.join("\n"),
  };
}

function localDateKey(value) {
  if (!value) return "";

  if (
    typeof value === "string" &&
    /^\d{4}-\d{2}-\d{2}$/.test(value)
  ) {
    return value;
  }

  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
}

function followupBucket(mockup) {
  const stage = mockup.outreach_stage || "not_contacted";

  if (stage === "replied") return "replied";
  if (stage === "activated") return "activated";
  if (!mockup.next_followup_at) return "unscheduled";

  const followupDate = localDateKey(mockup.next_followup_at);
  const today = localDateKey(new Date());

  if (!followupDate) return "unscheduled";
  if (followupDate < today) return "overdue";
  if (followupDate === today) return "today";

  return "upcoming";
}

function AdminShell({ children, onReset, staffMode = false, currentUser = null }) {
    const handleLogout = () => {
  handleUnauthorized();
};
  return (
    <div className="min-h-screen bg-[#09090B] text-white font-body" data-testid="admin-shell">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#09090B]/80 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded bg-[#00E5FF] text-black font-display font-bold text-xs">
              T
            </span>
            <span className="font-display font-semibold text-sm">
              TapToTable{" "}
              <span className="text-zinc-500 font-normal">
                · {staffMode ? "staff" : "admin"}
             </span>
            </span>
          </div>
          {staffMode && currentUser ? (
  <div className="flex items-center gap-3">
  <a
    href="/staff/leads"
    className="rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-3 py-1.5 text-xs font-semibold text-cyan-300 hover:bg-cyan-500/20"
  >
    Leads
  </a>

  <div className="flex items-center gap-3">
  <div className="text-xs text-zinc-400">
    Signed in as <span className="text-white">{currentUser.name}</span>
  </div>

  <button
    onClick={handleLogout}
    className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-xs font-semibold text-red-300 hover:bg-red-500/20"
  >
    Logout
  </button>
</div>
</div>
) : (
  <div className="flex items-center gap-3">
  <div className="text-xs text-zinc-400">
    Owner
  </div>

  <button
    onClick={handleLogout}
    className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-xs font-semibold text-red-300 hover:bg-red-500/20"
  >
    Logout
  </button>
</div>
)}
          
        </div>
       </header>
      <main className="max-w-6xl mx-auto px-6 md:px-10 py-10">{children}</main>
      <Toaster theme="dark" position="top-center" />
    </div>
  );
}

export default function AdminMockups({ staffMode = false }) {
  const [view, setView] = useState("list"); // "list" | "create"
  const [mockups, setMockups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  // Map mockup_id -> ConversionSummary for inline lead state.
  const [conversions, setConversions] = useState({});
  const [convertTarget, setConvertTarget] = useState(null);
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [openDmMenu, setOpenDmMenu] = useState(null);
  const [openActivationMenu, setOpenActivationMenu] = useState(null);
  const [showActivationGuide, setShowActivationGuide] = useState(false);
  const [showSalesWorkflow, setShowSalesWorkflow] = useState(false);
  const [activationChecklistSaving, setActivationChecklistSaving] = useState("");
  const [queueOpen, setQueueOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [queueFilter, setQueueFilter] = useState("all");
  const [buildLead, setBuildLead] = useState(null);
  const [creatingCampaignVariantId, setCreatingCampaignVariantId] = useState("");
  const [openCampaignGroup, setOpenCampaignGroup] = useState(null);
  const [campaignTypePicker, setCampaignTypePicker] = useState(null);
  const [sendingSuccessReminderId, setSendingSuccessReminderId] = useState("");
  const [customerReminderEmails, setCustomerReminderEmails] = useState({});
  const [savingCustomerReminderEmailId, setSavingCustomerReminderEmailId] = useState("");
  const currentUser = getCurrentUser();

useEffect(() => {
  if (!currentUser) {
    handleUnauthorized();
    return;
  }

  if (staffMode && currentUser.role === "owner") {
    window.location.replace("/admin/mockups");
    return;
  }

  if (!staffMode && currentUser.role !== "owner") {
    window.location.replace("/staff/mockups");
  }
}, [staffMode, currentUser]);

useEffect(() => {
  const params = new URLSearchParams(
    window.location.search
  );
  const leadId = params.get("buildLead");

  if (!leadId || !currentUser) {
    return undefined;
  }

  let cancelled = false;

  (async () => {
    try {
      const leadUrl = staffMode
        ? `${API}/staff/leads`
        : `${API}/leads`;

      const res = await fetch(leadUrl, {
        headers: authHeaders(),
      });

      if (res.status === 401 || res.status === 403) {
        handleUnauthorized();
        return;
      }

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const list = await res.json();
      const lead = Array.isArray(list)
        ? list.find((item) => item.id === leadId)
        : null;

      if (!lead) {
        throw new Error(
          "Lead not found or not assigned to this account."
        );
      }

      if (lead.source_mockup_id) {
        const editPath = staffMode
          ? `/staff/mockups/${lead.source_mockup_id}/edit`
          : `/admin/mockups/${lead.source_mockup_id}/edit`;

        window.location.replace(editPath);
        return;
      }

      if (!cancelled) {
        setBuildLead(lead);
        setView("create");

        window.history.replaceState(
          {},
          "",
          staffMode
            ? "/staff/mockups"
            : "/admin/mockups"
        );

        toast.success(
          "Lead loaded · review and create the Restaurant Page"
        );
      }
    } catch (err) {
      console.error(
        "Could not load inbound lead:",
        err
      );

      if (!cancelled) {
        toast.error(
          err?.message ||
            "Could not load this lead"
        );
      }
    }
  })();

  return () => {
    cancelled = true;
  };
}, [staffMode, currentUser]);

  useEffect(() => {
    if (!openActivationMenu) return undefined;

    const closeOnOutsideClick = (event) => {
      if (!(event.target instanceof Element)) return;

      const clickedInsideMenu = event.target.closest(
        `[data-activation-menu="${openActivationMenu}"]`
      );

      if (!clickedInsideMenu) {
        setOpenActivationMenu(null);
      }
    };

    document.addEventListener("mousedown", closeOnOutsideClick);

    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
    };
  }, [openActivationMenu]);

  const load = useCallback(async () => {
  setLoading(true);

  try {
    if (staffMode) {
      const res = await fetch(`${API}/mockups/staff/list`, {
        headers: authHeaders(),
      });

      if (res.status === 401 || res.status === 403) {
  handleUnauthorized();
  return;
}

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      setMockups(await res.json());
      setUsers([]);
      setConversions({});
      return;
    }

    const [mRes, cRes, uRes] = await Promise.all([
      fetch(`${API}/mockups`, { headers: authHeaders() }),
      fetch(`${API}/mockups/conversions`, { headers: authHeaders() }),
      fetch(`${API}/users`, { headers: authHeaders() }),
    ]);

    if (
  mRes.status === 401 ||
  mRes.status === 403 ||
  cRes.status === 401 ||
  cRes.status === 403 ||
  uRes.status === 401 ||
  uRes.status === 403
) {
  handleUnauthorized();
  return;
}

    if (!mRes.ok) {
      throw new Error(`HTTP ${mRes.status}`);
    }

    setMockups(await mRes.json());

    if (uRes.ok) {
      setUsers(await uRes.json());
    }

    if (cRes.ok) {
      const list = await cRes.json();
      const map = {};

      for (const conversion of list) {
        map[conversion.mockup_id] = conversion;
      }

      setConversions(map);
    }
  } catch (err) {
    console.error("Failed to load mockups:", err);
    toast.error("Could not load mockups");
  } finally {
    setLoading(false);
  }
}, [staffMode]);

  useEffect(() => {
  load();
}, [load]);


  const onDelete = async (m) => {
    if (!window.confirm(`Delete "${m.restaurant_name}"?`)) return;
    try {
      const res = await fetch(`${API}/mockups/${m.id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (res.status === 401 || res.status === 403) {
  handleUnauthorized();
  return;
}

if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setMockups((prev) => prev.filter((x) => x.id !== m.id));
      toast.success("Deleted");
    } catch (err) {
      console.error(err);
      toast.error("Delete failed");
    }
  };

  const previewUrl = (m) => `${window.location.origin}/mockup/${m.slug}`;

  const mainMockupId = (m) =>
    (m?.parent_mockup_id || "").trim() || m?.id || "";

  const conversionFor = (m) =>
    conversions[mainMockupId(m)] || null;

  const copyLink = async (m) => {
    try {
      await navigator.clipboard.writeText(previewUrl(m));
      toast.success("Link copied");
    } catch (err) {
      console.error(err);
      toast.error("Copy failed — select and copy manually");
    }
  };
  const copyLiveLink = async (conv) => {
  try {
    await navigator.clipboard.writeText(`${window.location.origin}${conv.funnel_path}`);
    toast.success("Live TapToTable Page link copied");
  } catch (err) {
    console.error(err);
    toast.error("Copy failed — select and copy manually");
  }
};

const createCampaignVariant = async (m, campaignType = "offer") => {
  if (!m?.id) return;

  setCreatingCampaignVariantId(m.id);

  try {
    const res = await fetch(
      `${API}/mockups/${m.id}/campaign-variant?campaign_type=${encodeURIComponent(
        campaignType
      )}`,
      {
        method: "POST",
        headers: authHeaders(),
      }
    );

    if (res.status === 401 || res.status === 403) {
      handleUnauthorized();
      return;
    }

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(data.detail || `HTTP ${res.status}`);
    }

    toast.success(`${data.campaign_label || "New campaign"} created`);
    setCampaignTypePicker(null);

    const editPath = staffMode
      ? `/staff/mockups/${data.mockup_id}/edit`
      : `/admin/mockups/${data.mockup_id}/edit`;

    window.location.assign(editPath);
  } catch (err) {
    console.error("Create campaign variant failed:", err);
    toast.error(err?.message || "Could not create another campaign");
  } finally {
    setCreatingCampaignVariantId("");
  }
};

const copyDM = async (m, type = "made_for_you") => {
  try {
    const text = buildInstagramDM({
      restaurant: m.restaurant_name || "your restaurant",
      city: m.city || "your area",
      demo: previewUrl(m),
      offer: m.offer || "your offer",
      type,
    });

    await navigator.clipboard.writeText(text);
    toast.success("DM pitch copied");
    setOpenDmMenu(null);
  } catch (err) {
    console.error("Copy DM failed:", err);
    toast.error("Copy failed");
  }
};

  const createClientPortal = async (m) => {
  try {
    const res = await fetch(
      `${API}/mockups/${m.id}/create-client-portal`,
      {
        method: "POST",
        headers: authHeaders(),
      }
    );

    if (res.status === 401 || res.status === 403) {
  handleUnauthorized();
  return;
}

if (!res.ok) {
  throw new Error(`HTTP ${res.status}`);
}

    const data = await res.json();

    await navigator.clipboard.writeText(
      `${window.location.origin}${data.client_url}`
    );

    toast.success("TapToTable Dashboard created and link copied");
    load();
  } catch (err) {
    console.error(err);
    toast.error("Failed to create TapToTable Dashboard");
  }
};

const copyClientPortal = async (m) => {
  if (!m.client_token) {
    toast.error("No TapToTable Dashboard yet");
    return;
  }

  try {
    await navigator.clipboard.writeText(
      `${window.location.origin}/client/${m.client_token}`
    );

    toast.success("TapToTable Dashboard link copied");
  } catch (err) {
    console.error(err);
    toast.error("Copy failed");
  }
};

const saveCustomerReminderEmail = async (m) => {
  if (!m?.id || staffMode) return;

  const email = String(
    customerReminderEmails[m.id] ?? m.customer_reminder_email ?? ""
  )
    .trim()
    .toLowerCase();

  if (
    email &&
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  ) {
    toast.error("Enter a valid Customer / Reminder Email");
    return;
  }

  setSavingCustomerReminderEmailId(m.id);

  try {
    const res = await fetch(`${API}/mockups/${m.id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders(),
      },
      body: JSON.stringify({
        customer_reminder_email: email,
      }),
    });

    if (res.status === 401 || res.status === 403) {
      handleUnauthorized();
      return;
    }

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(
        data.detail || `HTTP ${res.status}`
      );
    }

    setMockups((prev) =>
      prev.map((item) =>
        item.id === m.id
          ? {
              ...item,
              customer_reminder_email:
                data.customer_reminder_email ?? email,
              success_reminder_next_at:
                data.success_reminder_next_at ??
                item.success_reminder_next_at,
            }
          : item
      )
    );

    setCustomerReminderEmails((prev) => ({
      ...prev,
      [m.id]: data.customer_reminder_email ?? email,
    }));

    toast.success(
      email
        ? `Customer / Reminder Email saved: ${email}`
        : "Customer / Reminder Email cleared"
    );
  } catch (err) {
    console.error("Customer / Reminder Email save failed:", err);
    toast.error(
      err?.message ||
        "Could not save Customer / Reminder Email"
    );
  } finally {
    setSavingCustomerReminderEmailId("");
  }
};


const sendTestSuccessReminder = async (m) => {
  if (!m?.id) return;

  if (!m.client_token) {
    toast.error("Create the TapToTable Dashboard first");
    return;
  }

  setSendingSuccessReminderId(m.id);

  try {
    const res = await fetch(
      `${API}/mockups/${m.id}/success-reminders/send-test`,
      {
        method: "POST",
        headers: authHeaders(),
      }
    );

    if (res.status === 401 || res.status === 403) {
      handleUnauthorized();
      return;
    }

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(
        data.detail || `HTTP ${res.status}`
      );
    }

    toast.success(
      data.recipient
        ? `Test Success Reminder sent to ${data.recipient}`
        : "Test Success Reminder sent"
    );
  } catch (err) {
    console.error("Success Reminder test send failed:", err);
    toast.error(
      err?.message ||
        "Could not send test Success Reminder"
    );
  } finally {
    setSendingSuccessReminderId("");
  }
};


const copyActivationEmail = async (
  m,
  conv,
  type = "full",
  part = "body"
) => {
  if (!m.client_token) {
    toast.error("Create the TapToTable Dashboard first");
    return;
  }

  const restaurantName = m.restaurant_name || "Your Restaurant";
  const ownerName = m.contact_name || "Restaurant Owner";
  const publicPath = conv?.funnel_path || `/r/${m.slug}`;
  const publicUrl = `${window.location.origin}${publicPath}`;
  const dashboardUrl = `${window.location.origin}/client/${m.client_token}`;

  const templates = {
    full: {
      subject: `Your TapToTable Page Is Live — ${restaurantName}`,
      body: `Hello ${ownerName},

Your TapToTable setup for ${restaurantName} is complete and your page is ready.

Your live TapToTable Page:
${publicUrl}

Your secure TapToTable Dashboard:
${dashboardUrl}

Please keep your TapToTable Dashboard link private.

Your TapToTable Dashboard gives you everything you need to keep your page useful and up to date.

From your Dashboard you can:
• Create or update Promotions & Events whenever you want
• Use ready-to-copy promotion ideas when you need inspiration
• Change your page photo, page color, restaurant details, hours, menu, website, phone, address, and customer actions
• Preview your customer page before saving changes
• View customer activity and TapToTable performance analytics
• Turn optional Success Reminders on or off and choose how often TapToTable reminds you to refresh your page
• Use Help & Ideas for promotion suggestions, best practices, and guidance
• Contact TapToTable Support whenever you need help

A good habit is to refresh your Promotion or Event regularly so returning customers have a new reason to visit.

We recommend opening both links now, reviewing your Dashboard, and confirming that everything looks correct.

Tip: when you are not sure what to promote next, open Help & Ideas and use one of the copy-and-paste ideas as a starting point.

Need help or want us to review an update? Reply to this email or contact:

support@taptotable.co

Thank you for choosing TapToTable.

TapToTable Support
Melted Port Studios LLC`,
    },

    short: {
      subject: `Your TapToTable Page Is Ready — ${restaurantName}`,
      body: `Hello ${ownerName},

Your TapToTable Page for ${restaurantName} is ready.

Live TapToTable Page:
${publicUrl}

Secure TapToTable Dashboard:
${dashboardUrl}

Please keep your TapToTable Dashboard link private.

Your Dashboard lets you:
• Create or update Promotions & Events
• Use copy-and-paste promotion ideas
• Update photos, restaurant information, links, and customer actions
• View customer activity and analytics
• Set optional Success Reminders so TapToTable can remind you to refresh your page
• Open Help & Ideas whenever you need inspiration or guidance

Please review both links and reply if you need help. TapToTable Support is included.

TapToTable Support
support@taptotable.co`,
    },

    reminder: {
      subject: `Reminder: Sign In to Your TapToTable Dashboard — ${restaurantName}`,
      body: `Hello ${ownerName},

This is a quick reminder to open your TapToTable Dashboard for ${restaurantName} and confirm that you can access it.

Secure TapToTable Dashboard:
${dashboardUrl}

Live TapToTable Page:
${publicUrl}

Your TapToTable Dashboard lets you create or update Promotions & Events, use ready-to-copy ideas, update photos and restaurant details, manage customer actions, view analytics, and set optional Success Reminders to help you remember to refresh your page.

Open Help & Ideas in the Dashboard whenever you need inspiration or guidance.

Please keep the Dashboard link private. Reply to this email if you have trouble opening it or would like help with your first update. Support is included.

TapToTable Support
support@taptotable.co`,
    },

    followup: {
      subject: `Checking In — Your TapToTable Page & Dashboard`,
      body: `Hello ${ownerName},

I’m checking to make sure you received the TapToTable links for ${restaurantName}.

Live TapToTable Page:
${publicUrl}

Secure TapToTable Dashboard:
${dashboardUrl}

Please let us know that you received them and whether everything looks correct.

Your TapToTable Dashboard can be used for Promotions & Events, so you can keep your TapToTable Page current as your restaurant changes.

TapToTable Support
support@taptotable.co`,
    },
  };


  const selectedTemplate = templates[type] || templates.full;

  const bodyWithTraining = selectedTemplate.body.replace(
    dashboardUrl,
    `${dashboardUrl}

Watch Your TapToTable Dashboard Training Video:
https://taptotable.co/training

This step-by-step walkthrough shows you how to manage your page, create promotions and events, and understand customer activity. You can watch it on your phone or computer.`
  );

  const textToCopy =
    part === "subject"
      ? selectedTemplate.subject
      : bodyWithTraining;

  try {
    await navigator.clipboard.writeText(textToCopy);

    const label =
      type === "reminder"
        ? "2-day reminder"
        : type === "followup"
        ? "follow-up"
        : type === "short"
        ? "short activation"
        : "activation";

    toast.success(
      `${label.charAt(0).toUpperCase() + label.slice(1)} ${
        part === "subject" ? "subject" : "email"
      } copied`
    );
    setOpenActivationMenu(null);
  } catch (err) {
    console.error("Copy activation email failed:", err);
    toast.error("Copy failed");
  }
};

  const updateActivationChecklist = async (
    mockupId,
    conv,
    field
  ) => {
    const savingKey = `${mockupId}:${field}`;
    setActivationChecklistSaving(savingKey);

    try {
      const body =
        field === "verified"
          ? { activation_verified: true }
          : { activation_email_sent: true };

      const res = await fetch(`${API}/leads/${conv.lead_id}/payment`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        body: JSON.stringify(body),
      });

      if (res.status === 401 || res.status === 403) {
        handleUnauthorized();
        return;
      }

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${res.status}`);
      }

      const updated = await res.json();

      setConversions((prev) => ({
        ...prev,
        [mockupId]: {
          ...prev[mockupId],
          ...updated,
        },
      }));

      toast.success(
        field === "verified"
          ? "Live Page + Dashboard marked verified"
          : "Activation Email marked sent"
      );
    } catch (err) {
      console.error("Activation checklist update failed:", err);
      toast.error("Could not update activation checklist");
    } finally {
      setActivationChecklistSaving("");
    }
  };

  if (!currentUser) {
  return null;
}
  const visibleMockups = staffMode && currentUser
  ? mockups.filter(
      (m) =>
        m.assigned_to === currentUser.id ||
        m.owner_email === currentUser.email
    )
  : mockups;

  const mainMockups = visibleMockups.filter(
    (m) => !(m.campaign_variant || m.parent_mockup_id)
  );

  const campaignVariantsByParent = visibleMockups.reduce(
    (acc, m) => {
      if (!m.campaign_variant && !m.parent_mockup_id) {
        return acc;
      }

      const parentId = mainMockupId(m);
      if (!parentId) return acc;

      if (!acc[parentId]) acc[parentId] = [];
      acc[parentId].push(m);
      return acc;
    },
    {}
  );

  const filteredMockups = mainMockups
    .filter((m) => {
      const q = searchText.trim().toLowerCase();

      if (q) {
        const haystack = [
          m.restaurant_name,
          m.city,
          m.instagram_handle,
          m.owner_email,
          m.slug,
          m.cuisine,
          m.campaign_label,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        if (!haystack.includes(q)) {
          return false;
        }
      }

      if (dateRange !== "all") {
        const created = new Date(m.created_at || m.updated_at || 0);

        if (Number.isNaN(created.getTime())) {
          return false;
        }

        const now = new Date();
        const cutoff = new Date(now);

        if (dateRange === "7d") {
          cutoff.setDate(now.getDate() - 7);
        } else if (dateRange === "30d") {
          cutoff.setDate(now.getDate() - 30);
        } else if (dateRange === "90d") {
          cutoff.setDate(now.getDate() - 90);
        }

        if (created < cutoff) {
          return false;
        }
      }

      return true;
    })
    .sort((a, b) => {
      if (sortBy === "oldest") {
        return (
          new Date(a.created_at || 0).getTime() -
          new Date(b.created_at || 0).getTime()
        );
      }

      if (sortBy === "name") {
        return String(a.restaurant_name || "").localeCompare(
          String(b.restaurant_name || "")
        );
      }

      if (sortBy === "last_viewed") {
        return (
          new Date(b.last_viewed_at || 0).getTime() -
          new Date(a.last_viewed_at || 0).getTime()
        );
      }

      return (
        new Date(b.created_at || 0).getTime() -
        new Date(a.created_at || 0).getTime()
      );
    });
  const followupQueue = visibleMockups
  .filter((m) => {
    // Campaign ideas share the main restaurant's outreach record.
    // Only the main Restaurant Page belongs in the daily queue.
    if (m.campaign_variant || m.parent_mockup_id) {
      return false;
    }

    const stage = m.outreach_stage || "not_contacted";
    const attempts = Number(m.outreach_attempt_count || 0);

    if (stage === "not_contacted" && attempts === 0) {
      return false;
    }

    return ["overdue", "today", "upcoming"].includes(
      followupBucket(m)
    );
  })
  .sort((a, b) => {
    const aTime = new Date(
      a.next_followup_at || 0
    ).getTime();

    const bTime = new Date(
      b.next_followup_at || 0
    ).getTime();

    return aTime - bTime;
  });

const overdueCount = followupQueue.filter(
  (m) => followupBucket(m) === "overdue"
).length;

const dueTodayCount = followupQueue.filter(
  (m) => followupBucket(m) === "today"
).length;

const upcomingCount = followupQueue.filter(
  (m) => followupBucket(m) === "upcoming"
).length;

const displayedFollowupQueue =
  queueFilter === "all"
    ? followupQueue
    : followupQueue.filter(
        (m) => followupBucket(m) === queueFilter
      );

  return (
    <AdminShell
      onReset={() => {}}
      staffMode={staffMode}
      currentUser={currentUser}
    >
      <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#00E5FF]">Restaurant Pages</div>
          <h1 className="mt-1 font-display text-2xl font-semibold leading-tight sm:text-3xl">Sales outreach Restaurant Pages</h1>
          <p className="text-sm text-zinc-400 mt-2 max-w-xl">
            Build personalized TapToTable Restaurant Pages, send outreach, and manage scheduled follow-ups.
          </p>
        </div>
        <div className="flex w-full flex-wrap gap-2 lg:w-auto lg:justify-end">
        <Link
          to={staffMode ? "/staff/leads" : "/admin/leads"}
          className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-4 py-2.5 text-sm font-semibold text-cyan-300 transition-colors hover:bg-cyan-500/20 sm:flex-none"
        >
          ← Back to Leads
        </Link>

        <button
          onClick={() => {
            if (view === "create") {
              setView("list");
              setBuildLead(null);
              return;
            }

            setBuildLead(null);
            setView("create");
          }}
          className="inline-flex flex-1 items-center justify-center gap-2 bg-[#00E5FF] text-black font-semibold text-sm px-4 py-2.5 rounded-full hover:bg-[#66FFFF] transition-colors sm:flex-none"
          data-testid="admin-new-mockup-toggle"
        >
          {view === "create" ? "Cancel" : "+ New Restaurant Page"}
        </button>
        <button
  onClick={() => setShowDraftModal(true)}
  className="inline-flex flex-1 items-center justify-center gap-2 bg-white/5 border border-white/10 text-white font-medium text-sm px-4 py-2.5 rounded-full hover:bg-white/10 transition-colors sm:flex-none"
  data-testid="admin-generate-draft-toggle"
  title="Auto-fill a Restaurant Page from a public Instagram handle"
>
  ✨ Generate Draft
</button>

{!staffMode && (
  <>
    <Link
      to="/admin/paid-ads"
      className="inline-flex flex-1 items-center justify-center gap-2 bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 font-medium text-sm px-4 py-2.5 rounded-full hover:bg-emerald-500/25 transition-colors sm:flex-none"
    >
      Paid Ads
    </Link>
    
        <Link
          to="/admin/manual"
          className="inline-flex flex-none items-center justify-center gap-2 bg-amber-500/15 border border-amber-500/30 text-amber-300 font-medium text-sm px-4 py-2.5 rounded-full hover:bg-amber-500/25 transition-colors whitespace-nowrap"
        >
          Operations Manual
        </Link>

    <Link
      to="/admin/success-center"
      className="inline-flex flex-1 items-center justify-center gap-2 bg-purple-500/15 border border-purple-500/30 text-purple-300 font-medium text-sm px-4 py-2.5 rounded-full hover:bg-purple-500/25 transition-colors sm:flex-none"
    >
      Success Center
    </Link>
    <Link
  to="/admin/client-emails"
  className="inline-flex flex-1 items-center justify-center gap-2 bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 font-medium text-sm px-4 py-2.5 rounded-full hover:bg-cyan-500/25 transition-colors sm:flex-none"
>
  Client Emails
</Link>
  </>
)}
        </div>


      </div>

           {view === "create" && (
        <div className="mb-10" data-testid="admin-create-panel">
          {buildLead ? (
            <div className="mb-4 rounded-2xl border border-cyan-400/30 bg-cyan-400/10 p-4">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-300">
                Inbound Lead
              </div>
              <div className="mt-1 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="font-display text-lg font-semibold text-white">
                    {buildLead.restaurant_name}
                  </div>
                  <div className="text-xs text-zinc-400">
                    {buildLead.email}
                    {" · "}
                    {buildLead.campaign_type === "event"
                      ? "Event request"
                      : "Promotion / Offer request"}
                  </div>
                </div>

                <Link
                  to={staffMode ? "/staff/leads" : "/admin/leads"}
                  className="mt-2 inline-flex w-fit rounded-full border border-white/10 bg-white/5 px-3 py-2 text-xs font-semibold text-white hover:bg-white/10 sm:mt-0"
                >
                  Back to Lead
                </Link>
              </div>
            </div>
          ) : null}

          <MockupForm
            key={buildLead?.id || "manual-create"}
            mode="create"
            initialValues={initialValuesFromInboundLead(
              buildLead,
              staffMode,
              currentUser
            )}
            onSuccess={() => {
              setBuildLead(null);
              setView("list");
              load();
            }}
          />
        </div>
      )}

      <div className="mb-6 rounded-2xl border border-white/10 bg-[#121214]">
        <button
          type="button"
          onClick={() => setQueueOpen((current) => !current)}
          className="flex w-full items-center justify-between gap-4 p-5 text-left"
        >
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#00E5FF]">
              Daily Follow-Up Queue
            </div>

            <div className="mt-1 flex flex-wrap items-center gap-2">
              <h2 className="font-display text-xl font-semibold">
                Outreach requiring attention
              </h2>

              <span className="rounded-full border border-red-500/30 bg-red-500/10 px-2 py-1 text-[10px] font-semibold text-red-300">
                Overdue {overdueCount}
              </span>

              <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-[10px] font-semibold text-amber-300">
                Today {dueTodayCount}
              </span>

              <span className="rounded-full border border-cyan-500/30 bg-cyan-500/10 px-2 py-1 text-[10px] font-semibold text-cyan-300">
                Upcoming {upcomingCount}
              </span>
            </div>
          </div>

          <span className="text-sm font-semibold text-cyan-300">
            {queueOpen ? "Hide ▲" : "Show ▼"}
          </span>
        </button>

        {queueOpen && (
          <div className="border-t border-white/10 p-5">
            <p className="text-sm text-zinc-400">
              Select a category to show only those scheduled Restaurant Pages.
            </p>

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() =>
                  setQueueFilter((current) =>
                    current === "overdue" ? "all" : "overdue"
                  )
                }
                className={`cursor-pointer rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                  queueFilter === "overdue"
                    ? "border-red-400 bg-red-500/30 text-red-100 ring-1 ring-red-400/40"
                    : "border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500/20"
                }`}
              >
                Overdue: {overdueCount}
              </button>

              <button
                type="button"
                onClick={() =>
                  setQueueFilter((current) =>
                    current === "today" ? "all" : "today"
                  )
                }
                className={`cursor-pointer rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                  queueFilter === "today"
                    ? "border-amber-400 bg-amber-500/30 text-amber-100 ring-1 ring-amber-400/40"
                    : "border-amber-500/30 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20"
                }`}
              >
                Due Today: {dueTodayCount}
              </button>

              <button
                type="button"
                onClick={() =>
                  setQueueFilter((current) =>
                    current === "upcoming" ? "all" : "upcoming"
                  )
                }
                className={`cursor-pointer rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                  queueFilter === "upcoming"
                    ? "border-cyan-400 bg-cyan-500/30 text-cyan-100 ring-1 ring-cyan-400/40"
                    : "border-cyan-500/30 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20"
                }`}
              >
                Upcoming: {upcomingCount}
              </button>

              {queueFilter !== "all" && (
                <button
                  type="button"
                  onClick={() => setQueueFilter("all")}
                  className="cursor-pointer rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-zinc-300 transition-colors hover:bg-white/10"
                >
                  Show All
                </button>
              )}
            </div>

            {displayedFollowupQueue.length === 0 ? (
              <div className="mt-5 rounded-xl border border-white/5 bg-black/20 px-4 py-5 text-sm text-zinc-400">
                {queueFilter === "all"
                  ? "No scheduled follow-ups are due or upcoming."
                  : `No ${
                      queueFilter === "today" ? "due today" : queueFilter
                    } follow-ups found.`}
              </div>
            ) : (
              <div className="mt-5 grid gap-3">
                {displayedFollowupQueue.map((m) => {
                  const bucket = followupBucket(m);

                  return (
                    <div
                      key={`followup-${m.id}`}
                      className="flex flex-col gap-4 rounded-xl border border-white/5 bg-black/20 p-4 md:flex-row md:items-center md:justify-between"
                    >
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="truncate font-display font-semibold">
                            {m.restaurant_name}
                          </div>

                          <RestaurantStatusBadge
                            mockup={m}
                            conversion={conversions[m.id]}
                          />

                          <span
                            className={`rounded-full border px-2 py-1 text-[10px] font-bold uppercase tracking-wide ${
                              bucket === "overdue"
                                ? "border-red-500/30 bg-red-500/10 text-red-300"
                                : bucket === "today"
                                ? "border-amber-500/30 bg-amber-500/10 text-amber-300"
                                : "border-cyan-500/30 bg-cyan-500/10 text-cyan-300"
                            }`}
                          >
                            {bucket === "overdue"
                              ? "Overdue"
                              : bucket === "today"
                              ? "Due Today"
                              : "Upcoming"}
                          </span>
                        </div>

                        <div className="mt-1 text-xs text-zinc-400">
                          Current stage:{" "}
                          <span className="font-semibold text-zinc-200">
                            {outreachStageLabel(m.outreach_stage)}
                          </span>
                          {" · "}
                          Next follow-up:{" "}
                          <span className="font-semibold text-zinc-200">
                            {new Date(
                              m.next_followup_at
                            ).toLocaleDateString()}
                          </span>
                          {" · "}
                          Attempts:{" "}
                          <span className="font-semibold text-zinc-200">
                            {m.outreach_attempt_count ?? 0}
                          </span>
                        </div>
                      </div>

                      <Link
                        to={
                          staffMode
                            ? `/staff/mockups/${m.id}/edit`
                            : `/admin/mockups/${m.id}/edit`
                        }
                        className="inline-flex shrink-0 items-center justify-center rounded-full border border-[#00E5FF]/30 bg-[#00E5FF]/15 px-4 py-2 text-xs font-semibold text-[#00E5FF] transition-colors hover:bg-[#00E5FF]/25"
                      >
                        Continue Outreach
                      </Link>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <section>
        <div className="mb-4 rounded-2xl border border-white/10 bg-[#121214]">
          <div className="flex flex-col gap-4 p-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="font-display font-semibold text-lg">
                {staffMode ? "My Restaurant Pages" : "All Restaurant Pages"}{" "}
                <span className="text-zinc-500 text-sm font-normal">
                  ({filteredMockups.length} restaurants · {visibleMockups.length - mainMockups.length} campaign ideas)
                </span>
              </h2>

              <p className="mt-1 text-xs text-zinc-500">
                Search, filter by age, or sort without scrolling through every page.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setFiltersOpen((current) => !current)}
                className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold text-white hover:bg-white/10"
              >
                {filtersOpen ? "Hide Filters ▲" : "Search & Sort ▼"}
              </button>

              <button
                onClick={load}
                className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs text-zinc-300 hover:bg-white/10 hover:text-white"
                data-testid="admin-refresh"
              >
                Refresh
              </button>
            </div>
          </div>

          {filtersOpen && (
            <div className="grid gap-3 border-t border-white/10 p-4 md:grid-cols-3">
              <div>
                <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-500">
                  Search
                </label>

                <input
                  value={searchText}
                  onChange={(event) => setSearchText(event.target.value)}
                  placeholder="Restaurant, city, handle, email..."
                  className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white placeholder-zinc-600 focus:border-cyan-400 focus:outline-none"
                />
              </div>

              <div>
                <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-500">
                  Created
                </label>

                <select
                  value={dateRange}
                  onChange={(event) => setDateRange(event.target.value)}
                  className="w-full rounded-xl border border-white/10 bg-[#1A1A1F] px-4 py-3 text-sm text-white focus:border-cyan-400 focus:outline-none"
                >
                  <option value="all">All time</option>
                  <option value="7d">Last 7 days</option>
                  <option value="30d">Last 30 days</option>
                  <option value="90d">Last 90 days</option>
                </select>
              </div>

              <div>
                <label className="mb-2 block text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-500">
                  Sort
                </label>

                <select
                  value={sortBy}
                  onChange={(event) => setSortBy(event.target.value)}
                  className="w-full rounded-xl border border-white/10 bg-[#1A1A1F] px-4 py-3 text-sm text-white focus:border-cyan-400 focus:outline-none"
                >
                  <option value="newest">Newest first</option>
                  <option value="oldest">Oldest first</option>
                  <option value="name">Restaurant name A–Z</option>
                  <option value="last_viewed">Most recently viewed</option>
                </select>
              </div>

              {(searchText || dateRange !== "all" || sortBy !== "newest") && (
                <div className="md:col-span-3">
                  <button
                    type="button"
                    onClick={() => {
                      setSearchText("");
                      setDateRange("all");
                      setSortBy("newest");
                    }}
                    className="text-xs font-semibold text-cyan-300 hover:text-cyan-200"
                  >
                    Clear filters
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-zinc-500 text-sm py-8">Loading…</div>
        ) : filteredMockups.length === 0 ? (
          <div className="bg-[#121214] border border-white/5 rounded-2xl p-10 text-center text-zinc-400">
            No Restaurant Pages yet. Create your first one to start outreach.
          </div>
        ) : (
          <ul className="grid gap-3" data-testid="admin-mockup-list">
            {filteredMockups.map((m) => {
              const conv = conversionFor(m);
              const isCampaignVariant = false;
              const campaignVariants = campaignVariantsByParent[m.id] || [];
              const lastViewed = m.last_viewed_at
                ? new Date(m.last_viewed_at).toLocaleString()
                : "never";

              return (
                <li
                  key={m.id}
                  className="relative overflow-hidden bg-[#121214] border border-white/5 rounded-2xl p-5 pl-7 hover:border-[#00E5FF]/30 transition-colors"
                  data-testid={`admin-mockup-row-${m.slug}`}
                >
                  <div
                    className="absolute bottom-0 left-0 top-0 w-1.5 bg-[#00E5FF]"
                    aria-hidden="true"
                  />
                  <div className="flex flex-col gap-4">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-end gap-2">
                        <div className="min-w-0">
                          <div className="mb-0.5 text-[9px] font-bold uppercase tracking-[0.18em] text-cyan-400/70">
                            Restaurant
                          </div>
                          <div className="truncate font-display text-xl font-bold leading-tight text-white">
                            {m.restaurant_name}
                          </div>
                        </div>

                        {isCampaignVariant ? (
                          <span
                            className="inline-flex shrink-0 rounded-full border border-fuchsia-400/35 bg-fuchsia-400/15 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-fuchsia-300"
                            title="Additional promotion/demo idea for this restaurant"
                          >
                            Campaign Idea
                          </span>
                        ) : (
                          <RestaurantStatusBadge
                            mockup={m}
                            conversion={conv}
                          />
                        )}
                      </div>

                      <div className="text-xs text-zinc-400 mt-1">
                        {m.city || "—"} ·{" "}
                        {m.instagram_handle ? `@${m.instagram_handle}` : "no handle"} · /mockup/{m.slug}
                      </div>

                      {isCampaignVariant ? (
                        <div className="mt-2 inline-flex items-center gap-2 rounded-lg border border-fuchsia-400/20 bg-fuchsia-400/[0.07] px-3 py-2 text-xs text-fuchsia-200">
                          <span className="font-semibold">
                            {m.campaign_label || "Promotion Idea"}
                          </span>
                          <span className="text-fuchsia-300/60">
                            · same restaurant, separate demo
                          </span>
                        </div>
                      ) : null}

                      <div className="mt-2 flex flex-wrap gap-2">
                        <span className="text-[10px] uppercase tracking-wide bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 rounded-full px-2 py-1">
                          Assigned: {
                            !m.assigned_to || m.assigned_to === "owner"
                              ? "Owner"
                              : (
                                  users.find((u) => u.id === m.assigned_to)?.name ||
                                  m.owner_email ||
                                  m.assigned_to
                                )
                          }
                        </span>
                      </div>

                      <div
                        className="text-[11px] text-zinc-500 mt-1"
                        data-testid={`admin-mockup-views-${m.slug}`}
                      >
                        <div className="flex flex-col gap-1">
                          <div>
                            <span className="text-zinc-400">Restaurant Page:</span>{" "}
                            Views: <span className="text-zinc-300">{m.view_count ?? 0}</span>
                            {" · "}Clicks: <span className="text-zinc-300">{m.click_count ?? 0}</span>
                            {" · "}Claims: <span className="text-zinc-300">{m.claim_count ?? 0}</span>
                            {" · "}Conv:{" "}
                            <span className="text-zinc-300">
                              {m.view_count > 0
                                ? `${Math.round(((m.claim_count ?? 0) / m.view_count) * 100)}%`
                                : "—"}
                            </span>
                          </div>

                          {conv && !isCampaignVariant ? (
                            <div>
                              <span className="text-cyan-300">Live:</span>{" "}
                              Visits: <span className="text-cyan-300">{conv.page_visits ?? 0}</span>
                              {" · "}Claims: <span className="text-emerald-300">{conv.offer_claims ?? 0}</span>
                              {" · "}Claim Rate:{" "}
                              <span className="text-emerald-300">
                                {(conv.page_visits ?? 0) > 0
                                  ? `${Math.round(((conv.offer_claims ?? 0) / (conv.page_visits ?? 1)) * 100)}%`
                                  : "—"}
                              </span>
                            </div>
                          ) : null}

                          {isCampaignVariant && conv ? (
                            <div className="text-fuchsia-300/80">
                              Uses the main restaurant&apos;s live account.
                              Demo analytics above are for this idea only.
                            </div>
                          ) : null}

                          <div>
                            Last viewed: <span className="text-zinc-300">{lastViewed}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
{isCampaignVariant ? (
  <div className="w-full rounded-xl border border-fuchsia-400/15 bg-fuchsia-400/[0.05] px-3 py-2 text-[11px] leading-5 text-fuchsia-200/80">
    Sales demo only · outreach, payment, activation, and the Client Dashboard remain on the main Restaurant Page.
  </div>
) : null}

<a
  href={`/mockup/${m.slug}`}
  target="_blank"
  rel="noreferrer"
  className="text-xs bg-white/5 border border-white/10 rounded-full px-3 py-1.5 hover:bg-white/10 transition-colors"
  data-testid={`admin-preview-${m.slug}`}
>
  Restaurant Page ↗
</a>

<button
  onClick={() => copyLink(m)}
  className="text-xs bg-white/5 border border-white/10 rounded-full px-3 py-1.5 hover:bg-white/10 transition-colors"
  data-testid={`admin-copy-link-${m.slug}`}
>
  Copy Restaurant Page Link
</button>

<button
  type="button"
  onClick={() =>
    setOpenCampaignGroup(openCampaignGroup === m.id ? null : m.id)
  }
  className="text-xs bg-fuchsia-500/15 border border-fuchsia-500/30 text-fuchsia-300 rounded-full px-3 py-1.5 hover:bg-fuchsia-500/25 transition-colors"
>
  Campaigns: {1 + campaignVariants.length} {openCampaignGroup === m.id ? "▲" : "▼"}
</button>

<div className="relative">
  <button
    type="button"
    onClick={() =>
      setCampaignTypePicker(campaignTypePicker === m.id ? null : m.id)
    }
    disabled={creatingCampaignVariantId === m.id}
    className="text-xs bg-fuchsia-500/15 border border-fuchsia-500/30 text-fuchsia-300 rounded-full px-3 py-1.5 hover:bg-fuchsia-500/25 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
  >
    {creatingCampaignVariantId === m.id
      ? "Creating Campaign..."
      : "+ Create New Campaign"}
  </button>

  {campaignTypePicker === m.id ? (
    <div className="absolute left-0 z-50 mt-2 w-56 overflow-hidden rounded-xl border border-white/10 bg-[#121214] shadow-xl">
      <button
        type="button"
        onClick={() => createCampaignVariant(m, "offer")}
        className="block w-full px-4 py-3 text-left text-xs text-zinc-200 hover:bg-white/10"
      >
        Promotion / Offer
      </button>
      <button
        type="button"
        onClick={() => createCampaignVariant(m, "event")}
        className="block w-full border-t border-white/10 px-4 py-3 text-left text-xs text-zinc-200 hover:bg-white/10"
      >
        Event
      </button>
    </div>
  ) : null}
</div>

{conv && !isCampaignVariant ? (
  <button
    onClick={() => copyLiveLink(conv)}
    className="text-xs bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 rounded-full px-3 py-1.5 hover:bg-cyan-500/25 transition-colors"
    title="Copy the live TapToTable Page link"
  >
    Copy Live Page
  </button>
) : null}

<div className="relative">
  <button
    onClick={() => setOpenDmMenu(openDmMenu === m.id ? null : m.id)}
    className="text-xs bg-[#00E5FF]/15 border border-[#00E5FF]/30 text-[#00E5FF] rounded-full px-3 py-1.5 hover:bg-[#00E5FF]/25 transition-colors"
    data-testid={`admin-copy-dm-${m.slug}`}
    title="Copy ready-to-send Instagram DM"
  >
    Copy DM Message▾
  </button>

  {openDmMenu === m.id && (
    <div className="absolute right-0 z-50 mt-2 w-56 rounded-xl border border-white/10 bg-[#121214] shadow-xl overflow-hidden">
      {[
        ["made_for_you", "Made this for you"],
        ["friendly_intro", "Friendly intro"],
        ["quick_compliment", "Quick compliment"],
        ["free_offer_angle", "Free offer angle"],
        ["low_pressure", "Low pressure"],
        ["social_proof", "Social proof"],
        ["owner_to_owner", "Owner-to-owner"],
        ["limited_time", "Limited-time setup"],
        ["follow_up_1", "Follow-up #1"],
        ["follow_up_2", "Follow-up #2"],
      ].map(([type, label]) => (
        <button
          key={type}
          onClick={() => copyDM(m, type)}
          className="block w-full text-left px-4 py-2 text-xs text-zinc-300 hover:bg-white/10 hover:text-white"
        >
          {label}
        </button>
      ))}
    </div>
  )}
</div>

{!isCampaignVariant && m.client_portal_enabled !== true ? (
  <button
    onClick={() => createClientPortal(m)}
    className="text-xs bg-amber-500/15 border border-amber-500/30 text-amber-300 rounded-full px-3 py-1.5 hover:bg-amber-500/25 transition-colors"
    title="Create the restaurant's private TapToTable Dashboard"
  >
    Create TapToTable Dashboard
  </button>
) : !isCampaignVariant ? (
  <>
    <button
      onClick={() => copyClientPortal(m)}
      className="text-xs bg-purple-500/15 border border-purple-500/30 text-purple-300 rounded-full px-3 py-1.5 hover:bg-purple-500/25 transition-colors"
      title="Copy the private TapToTable Dashboard link"
    >
      Copy TapToTable Dashboard
    </button>

    {!staffMode ? (
      <button
        type="button"
        onClick={() => sendTestSuccessReminder(m)}
        disabled={sendingSuccessReminderId === m.id}
        className="text-xs rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 font-semibold text-emerald-300 transition-colors hover:bg-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-50"
        data-testid={`admin-send-test-success-reminder-${m.slug}`}
        title="Send a test Success Reminder to the saved Customer / Reminder Email"
      >
        {sendingSuccessReminderId === m.id
          ? "Sending Reminder..."
          : "Send Test Success Reminder"}
      </button>
    ) : null}

    <div
      className="relative"
      data-activation-menu={m.id}
    >
      <button
        type="button"
        onClick={() =>
          setOpenActivationMenu(
            openActivationMenu === m.id ? null : m.id
          )
        }
        className="text-xs bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 rounded-full px-3 py-1.5 hover:bg-emerald-500/25 transition-colors"
        title="Copy a ready-to-send activation email"
      >
        Activation Email ▾
      </button>

      {openActivationMenu === m.id && (
        <div className="fixed left-4 right-4 top-20 z-[80] max-h-[calc(100dvh-6rem)] overflow-y-auto overscroll-contain rounded-xl border border-white/10 bg-[#121214] shadow-2xl sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:z-50 sm:mt-2 sm:w-80 sm:max-h-[70vh]">
          <div className="sticky top-0 z-10 flex items-center justify-between border-b border-white/10 bg-[#121214] px-3 py-2">
            <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-500">
              Activation Emails
            </div>

            <button
              type="button"
              onClick={() => setOpenActivationMenu(null)}
              className="rounded-md border border-white/10 bg-white/5 px-2 py-1 text-xs text-zinc-300 hover:bg-white/10 hover:text-white"
              aria-label="Close activation email menu"
            >
              × Close
            </button>
          </div>

          {[
            ["full", "Full Activation Email"],
            ["short", "Short Activation Email"],
            ["reminder", "2-Day Dashboard Access Reminder"],
            ["followup", "No-Reply Follow-Up"],
          ].map(([type, label]) => (
            <div
              key={type}
              className="border-b border-white/10 p-3 last:border-b-0"
            >
              <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-500">
                {label}
              </div>

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() =>
                    copyActivationEmail(m, conv, type, "subject")
                  }
                  className="flex-1 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-left text-xs text-zinc-300 hover:bg-white/10 hover:text-white"
                >
                  Copy Subject
                </button>

                <button
                  type="button"
                  onClick={() =>
                    copyActivationEmail(m, conv, type, "body")
                  }
                  className="flex-1 rounded-lg border border-emerald-500/30 bg-emerald-500/15 px-3 py-2 text-left text-xs text-emerald-300 hover:bg-emerald-500/25"
                >
                  Copy Email
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  </>
) : null}

                      {!conv && (
                        <button
                          onClick={() => setConvertTarget(m)}
                          className="text-xs bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 rounded-full px-3 py-1.5 hover:bg-emerald-500/25 transition-colors"
                          data-testid={`admin-convert-${m.slug}`}
                          title="Convert this Restaurant Page into a sales lead"
                        >
                          Convert to Sales Lead
                        </button>
                      )}
                      {conv && (
                        <span
                          className="text-xs bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 rounded-full px-3 py-1.5"
                          data-testid={`admin-converted-badge-${m.slug}`}
                        >
                          Converted ✓
                        </span>
                      )}
                      <Link
                        to={
                          staffMode
                            ? `/staff/mockups/${m.id}/edit`
                            : `/admin/mockups/${m.id}/edit`
                      }
                        className="text-xs bg-white/5 border border-white/10 rounded-full px-3 py-1.5 hover:bg-white/10 transition-colors"
                        data-testid={`admin-edit-${m.slug}`}
                      >
                        Edit Page & Outreach
                      </Link>
                      <button
                        onClick={() => onDelete(m)}
                        className="text-xs bg-red-500/10 border border-red-500/30 text-red-300 rounded-full px-3 py-1.5 hover:bg-red-500/20 transition-colors"
                        data-testid={`admin-delete-${m.slug}`}
                      >
                        Delete
                      </button>
                    </div>

                    {openCampaignGroup === m.id ? (
                      <div className="w-full rounded-2xl border border-fuchsia-400/20 bg-fuchsia-400/[0.04] p-4">
                        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-fuchsia-300">
                              Campaigns
                            </div>
                            <div className="mt-1 text-sm font-semibold text-white">
                              Choose a campaign to preview or edit
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setOpenCampaignGroup(null)}
                            className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-zinc-300 hover:bg-white/10"
                          >
                            Close
                          </button>
                        </div>

                        <div className="grid gap-2">
                          <div className="rounded-xl border border-cyan-400/20 bg-cyan-400/[0.05] p-3">
                            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                              <div>
                                <div className="text-xs font-semibold text-white">Main Campaign</div>
                                <div className="mt-1 text-[11px] text-zinc-400">
                                  Current restaurant page · account and live-page owner
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <a href={`/mockup/${m.slug}`} target="_blank" rel="noreferrer"
                                  className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-zinc-200 hover:bg-white/10">
                                  Preview
                                </a>
                                <Link
                                  to={staffMode ? `/staff/mockups/${m.id}/edit` : `/admin/mockups/${m.id}/edit`}
                                  className="rounded-full border border-cyan-400/25 bg-cyan-400/10 px-3 py-1.5 text-xs font-semibold text-cyan-300 hover:bg-cyan-400/15">
                                  Edit
                                </Link>
                              </div>
                            </div>
                          </div>

                          {campaignVariants.length === 0 ? (
                            <div className="rounded-xl border border-white/10 bg-black/20 px-4 py-4 text-xs text-zinc-400">
                              No additional campaign ideas yet.
                            </div>
                          ) : (
                            campaignVariants.slice().sort(
                              (a, b) =>
                                new Date(a.created_at || 0).getTime() -
                                new Date(b.created_at || 0).getTime()
                            ).map((campaign) => (
                              <div key={campaign.id}
                                className="rounded-xl border border-fuchsia-400/15 bg-black/20 p-3">
                                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                                  <div className="min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <span className="text-xs font-semibold text-white">
                                        {campaign.campaign_label ||
                                          (campaign.campaign_type === "event" ? "Event Campaign" : "Promotion Idea")}
                                      </span>
                                      <span className="rounded-full border border-fuchsia-400/25 bg-fuchsia-400/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-fuchsia-300">
                                        {campaign.campaign_type === "event" ? "Event" : "Promotion"}
                                      </span>
                                    </div>
                                    <div className="mt-1 truncate text-[11px] text-zinc-500">
                                      /mockup/{campaign.slug}
                                    </div>
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    <a href={`/mockup/${campaign.slug}`} target="_blank" rel="noreferrer"
                                      className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-zinc-200 hover:bg-white/10">
                                      Preview
                                    </a>
                                    <Link
                                      to={staffMode ? `/staff/mockups/${campaign.id}/edit` : `/admin/mockups/${campaign.id}/edit`}
                                      className="rounded-full border border-fuchsia-400/25 bg-fuchsia-400/10 px-3 py-1.5 text-xs font-semibold text-fuchsia-300 hover:bg-fuchsia-400/15">
                                      Edit
                                    </Link>
                                    <button
                                      type="button"
                                      onClick={() => onDelete(campaign)}
                                      className="rounded-full border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-xs font-semibold text-red-300 hover:bg-red-500/20"
                                      data-testid={`admin-delete-campaign-${campaign.slug}`}
                                      title="Delete this campaign idea only"
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    ) : null}
                  </div>

                  {conv && (() => {
                    const paymentReady =
                      conv.payment_status === "paid" &&
                      conv.account_status === "active";

                    const dashboardReady =
                      m.client_portal_enabled === true &&
                      Boolean(m.client_token);

                    const livePageVerified =
                      Boolean(conv.activation_verified_at);

                    const activationEmailSent =
                      Boolean(conv.activation_email_sent_at);

                    const activationComplete =
                      paymentReady &&
                      dashboardReady &&
                      livePageVerified &&
                      activationEmailSent;

                    const readyForVerification =
                      paymentReady && dashboardReady;

                    return (
                      <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.06] p-4">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-300">
                              Activation Checklist
                            </div>
                            <div className="mt-1 text-sm font-semibold text-white">
                              Paid customer handoff — follow these steps in order
                            </div>
                          </div>

                          {activationComplete ? (
                            <span className="rounded-full border border-emerald-400/40 bg-emerald-400/20 px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-emerald-200">
                              Activation Complete
                            </span>
                          ) : readyForVerification ? (
                            <span className="rounded-full border border-emerald-500/30 bg-emerald-500/15 px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-emerald-300">
                              Payment + Dashboard Ready
                            </span>
                          ) : (
                            <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-amber-300">
                              Activation In Progress
                            </span>
                          )}
                        </div>

                        <div className="mt-4 grid gap-2">
                          <div
                            className={`flex items-start gap-3 rounded-xl border p-3 ${
                              conv.payment_status === "paid"
                                ? "border-emerald-500/20 bg-emerald-500/[0.05]"
                                : "border-white/10 bg-black/20"
                            }`}
                          >
                            <span
                              className={`inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                                conv.payment_status === "paid"
                                  ? "bg-emerald-500 text-black"
                                  : "border border-white/15 bg-white/5 text-zinc-300"
                              }`}
                            >
                              {conv.payment_status === "paid" ? "✓" : "1"}
                            </span>
                            <div>
                              <div className="text-sm font-semibold text-white">
                                Confirm Stripe Payment
                              </div>
                              <p className="mt-1 text-xs leading-relaxed text-zinc-400">
                                Open Stripe and confirm the restaurant&apos;s $59/month subscription is active.
                              </p>
                            </div>
                          </div>

                          <div
                            className={`flex items-start gap-3 rounded-xl border p-3 ${
                              paymentReady
                                ? "border-emerald-500/20 bg-emerald-500/[0.05]"
                                : "border-white/10 bg-black/20"
                            }`}
                          >
                            <span
                              className={`inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                                paymentReady
                                  ? "bg-emerald-500 text-black"
                                  : "border border-white/15 bg-white/5 text-zinc-300"
                              }`}
                            >
                              {paymentReady ? "✓" : "2"}
                            </span>
                            <div>
                              <div className="text-sm font-semibold text-white">
                                Mark Restaurant Paid
                              </div>
                              <p className="mt-1 text-xs leading-relaxed text-zinc-400">
                                {paymentReady
                                  ? "Complete — this restaurant is marked Paid and Active."
                                  : "Use Mark Restaurant Paid in the Payment & Account panel below."}
                              </p>
                            </div>
                          </div>

                          {!staffMode && paymentReady ? (
                            <div className="rounded-xl border border-cyan-400/20 bg-cyan-400/[0.05] p-3">
                              <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
                                <div className="min-w-0 flex-1">
                                  <label
                                    htmlFor={`customer-reminder-email-${m.id}`}
                                    className="block text-sm font-semibold text-white"
                                  >
                                    Customer / Reminder Email
                                  </label>
                                  <p className="mt-1 text-xs leading-relaxed text-zinc-400">
                                    After payment, paste the real customer email from Stripe here.
                                    Success Reminders use only this address — never the sales lead email.
                                  </p>
                                  <input
                                    id={`customer-reminder-email-${m.id}`}
                                    type="email"
                                    value={
                                      customerReminderEmails[m.id] ??
                                      m.customer_reminder_email ??
                                      ""
                                    }
                                    onChange={(event) =>
                                      setCustomerReminderEmails((prev) => ({
                                        ...prev,
                                        [m.id]: event.target.value,
                                      }))
                                    }
                                    placeholder="customer@restaurant.com"
                                    className="mt-3 w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-white placeholder-zinc-600 focus:border-cyan-400 focus:outline-none"
                                    autoComplete="off"
                                    data-testid={`customer-reminder-email-${m.slug}`}
                                  />
                                  <p className="mt-2 text-[11px] leading-relaxed text-zinc-500">
                                    Billing and restaurant contacts can be different. Use the best
                                    day-to-day email for TapToTable reminders. You can change it later.
                                  </p>
                                </div>

                                <button
                                  type="button"
                                  onClick={() => saveCustomerReminderEmail(m)}
                                  disabled={
                                    savingCustomerReminderEmailId === m.id
                                  }
                                  className="shrink-0 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-4 py-2.5 text-xs font-semibold text-cyan-300 transition-colors hover:bg-cyan-400/20 disabled:cursor-not-allowed disabled:opacity-50"
                                  data-testid={`save-customer-reminder-email-${m.slug}`}
                                >
                                  {savingCustomerReminderEmailId === m.id
                                    ? "Saving…"
                                    : "Save Reminder Email"}
                                </button>
                              </div>
                            </div>
                          ) : null}

                          <div
                            className={`flex items-start gap-3 rounded-xl border p-3 ${
                              dashboardReady
                                ? "border-emerald-500/20 bg-emerald-500/[0.05]"
                                : "border-white/10 bg-black/20"
                            }`}
                          >
                            <span
                              className={`inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                                dashboardReady
                                  ? "bg-emerald-500 text-black"
                                  : "border border-white/15 bg-white/5 text-zinc-300"
                              }`}
                            >
                              {dashboardReady ? "✓" : "3"}
                            </span>
                            <div>
                              <div className="text-sm font-semibold text-white">
                                Create the TapToTable Dashboard
                              </div>
                              <p className="mt-1 text-xs leading-relaxed text-zinc-400">
                                {dashboardReady
                                  ? "Complete — the private TapToTable Dashboard is ready. Use Copy TapToTable Dashboard above."
                                  : "Click Create TapToTable Dashboard above. The private Dashboard link will be created and copied."}
                              </p>
                            </div>
                          </div>

                          <div
                            className={`flex flex-col gap-3 rounded-xl border p-3 sm:flex-row sm:items-center sm:justify-between ${
                              livePageVerified
                                ? "border-emerald-500/20 bg-emerald-500/[0.05]"
                                : "border-white/10 bg-black/20"
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <span
                                className={`inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                                  livePageVerified
                                    ? "bg-emerald-500 text-black"
                                    : "border border-white/15 bg-white/5 text-zinc-300"
                                }`}
                              >
                                {livePageVerified ? "✓" : "4"}
                              </span>
                              <div>
                                <div className="text-sm font-semibold text-white">
                                  Verify Live Page + Dashboard
                                </div>
                                <p className="mt-1 text-xs leading-relaxed text-zinc-400">
                                  {livePageVerified
                                    ? `Verified ${new Date(conv.activation_verified_at).toLocaleString()}.`
                                    : "Open the Live Page and TapToTable Dashboard. Confirm the restaurant name, promotion or event, photos, links, and saved changes are correct."}
                                </p>
                              </div>
                            </div>

                            {!livePageVerified && (
                              <button
                                type="button"
                                onClick={() =>
                                  updateActivationChecklist(
                                    m.id,
                                    conv,
                                    "verified"
                                  )
                                }
                                disabled={
                                  !readyForVerification ||
                                  Boolean(activationChecklistSaving)
                                }
                                className="shrink-0 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-4 py-2 text-xs font-semibold text-cyan-300 transition-colors hover:bg-cyan-400/20 disabled:cursor-not-allowed disabled:opacity-40"
                              >
                                {activationChecklistSaving === `${m.id}:verified`
                                  ? "Saving…"
                                  : "Mark Verified"}
                              </button>
                            )}
                          </div>

                          <div
                            className={`flex flex-col gap-3 rounded-xl border p-3 sm:flex-row sm:items-center sm:justify-between ${
                              activationEmailSent
                                ? "border-emerald-500/20 bg-emerald-500/[0.05]"
                                : "border-white/10 bg-black/20"
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <span
                                className={`inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                                  activationEmailSent
                                    ? "bg-emerald-500 text-black"
                                    : "border border-white/15 bg-white/5 text-zinc-300"
                                }`}
                              >
                                {activationEmailSent ? "✓" : "5"}
                              </span>
                              <div>
                                <div className="text-sm font-semibold text-white">
                                  Send the Activation Email
                                </div>
                                <p className="mt-1 text-xs leading-relaxed text-zinc-400">
                                  {activationEmailSent
                                    ? `Recorded as sent ${new Date(conv.activation_email_sent_at).toLocaleString()}.`
                                    : "Use Activation Email above. Copy the subject and email, send them to the owner, then record it here."}
                                </p>
                              </div>
                            </div>

                            {!activationEmailSent && (
                              <button
                                type="button"
                                onClick={() =>
                                  updateActivationChecklist(
                                    m.id,
                                    conv,
                                    "email"
                                  )
                                }
                                disabled={
                                  !livePageVerified ||
                                  !dashboardReady ||
                                  Boolean(activationChecklistSaving)
                                }
                                className="shrink-0 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-4 py-2 text-xs font-semibold text-emerald-300 transition-colors hover:bg-emerald-400/20 disabled:cursor-not-allowed disabled:opacity-40"
                              >
                                {activationChecklistSaving === `${m.id}:email`
                                  ? "Saving…"
                                  : "Mark Activation Email Sent"}
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="mt-3 rounded-xl border border-cyan-500/20 bg-cyan-500/[0.06] px-3 py-2 text-xs leading-relaxed text-zinc-300">
                          <span className="font-semibold text-cyan-300">V1 rule:</span>{" "}
                          payment confirmation is manual. Steps 4 and 5 are recorded manually and remain checked after refresh.
                        </div>
                      </div>
                    );
                  })()}

                  {conv && (
                    <MockupConversionPanel
                      conversion={conv}
                      onUpdate={(updated) =>
                        setConversions((prev) => ({ ...prev, [m.id]: updated }))
                      }
                    />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {convertTarget && (
        <ConvertMockupModal
          mockup={convertTarget}
          onClose={() => setConvertTarget(null)}
          onConverted={(data) => {
            setConversions((prev) => ({
              ...prev,
              [convertTarget.id]: {
                mockup_id: convertTarget.id,
                lead_id: data.lead_id,
                lead_slug: data.lead_slug,
                email: data.email,
                contact_name: "",
                phone: "",
                payment_link: data.payment_link || "",
                payment_status: data.payment_status,
                account_status: data.account_status,
                activation_verified_at: data.activation_verified_at || "",
                activation_email_sent_at: data.activation_email_sent_at || "",
                funnel_path: data.funnel_path,
                page_visits: 0,
                offer_claims: 0,
              },
            }));
            setConvertTarget(null);
            if (data.already_existed) {
              toast.message("Already converted — showing existing lead");
            } else {
              toast.success("Lead created · payment unpaid");
            }
          }}
        />
      )}

      {showDraftModal && (
        <GenerateDraftModal
          assignedUser={staffMode ? currentUser : null}
          onClose={() => setShowDraftModal(false)}
          onCreated={() => {
            setShowDraftModal(false);
            load();
          }}
        />
      )}

      <button
        type="button"
        onClick={() => setShowSalesWorkflow(true)}
        className="fixed bottom-20 right-4 z-40 inline-flex items-center gap-2 rounded-full border border-cyan-400/40 bg-cyan-400 px-4 py-3 text-xs font-semibold text-black shadow-2xl transition-all hover:bg-cyan-300 sm:bottom-6 sm:right-44 sm:px-5 sm:text-sm"
        aria-label="Open sales workflow"
      >
        Sales Workflow
      </button>

      {showSalesWorkflow && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setShowSalesWorkflow(false);
            }
          }}
        >
          <div className="max-h-[88vh] w-full max-w-4xl overflow-y-auto rounded-3xl border border-cyan-500/30 bg-[#0B1418] p-5 shadow-2xl md:p-7">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-cyan-300">
                  TapToTable Sales Workflow
                </div>

                <h2 className="mt-2 font-display text-2xl font-semibold text-white">
                  From finding a restaurant to activation
                </h2>

                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-zinc-400">
                  Follow this order so every lead is worked the same way.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setShowSalesWorkflow(false)}
                className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-zinc-300 hover:bg-white/10 hover:text-white"
              >
                × Close
              </button>
            </div>

            <div className="mt-6 grid gap-3 md:grid-cols-2">
              {[
                ["1. Find a restaurant", "Use Google with this formula: site:instagram.com + [RESTAURANT TYPE] + [CITY] + [STATE]. Example: site:instagram.com + pizza restaurant + Jacksonville + FL. Substitute the bracketed words for the restaurant type and market you want to search. Then record the Instagram handle and basic restaurant information."],
                ["2. Generate the Restaurant Page", "Click Generate Draft or + New Restaurant Page. Review the generated restaurant name, image, cuisine, links, and starting promotion or event before saving."],
                ["3. Review before outreach", "Open Restaurant Page ↗. Make sure it looks professional and the promotion, event, or customer action makes sense for that restaurant."],
                ["4. Send initial outreach", "Use Copy DM Message and send the first Instagram message. Then open Edit Page & Outreach and click Mark Sent so the system advances the sales stage."],
                ["5. Schedule the next follow-up", "Choose the required next follow-up date. The restaurant will then appear in the Daily Follow-Up Queue when it needs attention."],
                ["6. Work the Daily Follow-Up Queue", "Open Continue Outreach for overdue or due restaurants, send the recommended message, Mark Sent, and schedule the next date."],
                ["7. Handle replies", "If the owner responds, update the outreach stage to Replied and answer questions. Use the owner preview link when they are ready to review the page."],
                ["8. Convert the lead", "Use Convert to Sales Lead when the restaurant becomes a real sales opportunity so payment and account status can be tracked."],
                ["9. Send the owner review page", "Send the private /preview/ restaurant link. The owner reviews the customer experience, requests changes, or selects Activate My TapToTable Page."],
                ["10. Customer activates", "The owner pays $59/month through Stripe and lands on the Activation Confirmed page."],
                ["11. Complete activation", "Use the Activation Checklist on the restaurant card. Confirm Stripe payment, mark the restaurant Paid, create the TapToTable Dashboard, verify the Live Page and Dashboard, then send the Activation Email."],
                ["12. Follow up after activation", "Record completion. If the owner has not confirmed access, send the 2-Day Dashboard Reminder, then the No-Reply Follow-Up if still needed."],
              ].map(([title, body]) => (
                <div
                  key={title}
                  className="rounded-xl border border-white/10 bg-black/20 p-4"
                >
                  <div className="text-sm font-semibold text-white">
                    {title}
                  </div>

                  <p className="mt-2 text-xs leading-relaxed text-zinc-300">
                    {body}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-5 rounded-xl border border-cyan-400/25 bg-cyan-400/10 p-4">
              <div className="text-sm font-semibold text-cyan-300">
                Daily rule
              </div>

              <p className="mt-2 text-sm leading-relaxed text-zinc-300">
                Create new Restaurant Pages first, then work every overdue and
                due-today follow-up before stopping for the day.
              </p>
            </div>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setShowActivationGuide(true)}
        className="fixed bottom-4 right-4 z-40 inline-flex items-center gap-2 rounded-full border border-emerald-400/40 bg-emerald-500 px-4 py-3 text-xs font-semibold text-black shadow-2xl transition-all hover:bg-emerald-400 sm:bottom-6 sm:right-6 sm:px-5 sm:text-sm"
        aria-label="Open activation guide"
      >
        Activation Guide
      </button>

      {showActivationGuide && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setShowActivationGuide(false);
            }
          }}
        >
          <div className="max-h-[88vh] w-full max-w-4xl overflow-y-auto rounded-3xl border border-emerald-500/30 bg-[#0E1713] p-5 shadow-2xl md:p-7">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-300">
                  Manual Activation Handoff
                </div>

                <h2 className="mt-2 font-display text-2xl font-semibold text-white">
                  What to do after a restaurant pays
                </h2>
              </div>

              <button
                type="button"
                onClick={() => setShowActivationGuide(false)}
                className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-zinc-300 hover:bg-white/10 hover:text-white"
              >
                × Close
              </button>
            </div>

            <div className="mt-6 rounded-xl border border-white/10 bg-black/20 p-4">
              <div className="text-xs font-bold uppercase tracking-[0.16em] text-zinc-500">
                Real-Customer Sequence
              </div>

              <div className="mt-3 space-y-1 font-mono text-sm leading-relaxed text-zinc-200">
                <div>Confirm Stripe payment</div>
                <div>→ Match payment to restaurant</div>
                <div>→ Final page review</div>
                <div>→ Test live page</div>
                <div>→ Create and test TapToTable Dashboard</div>
                <div>→ Send Activation Email</div>
                <div>→ Record completion</div>
                <div>→ Send 2-day reminder if needed</div>
                <div>→ Send no-reply follow-up if needed</div>
              </div>
            </div>

            <div className="mt-5 grid gap-3 md:grid-cols-2">
              {[
                ["1. Confirm payment", "In Stripe, confirm the $59 subscription is active. Record the customer email, payment date, and subscription reference."],
                ["2. Match the restaurant", "Open the correct Restaurant Page and verify the restaurant name, owner email, Instagram handle, and Stripe checkout email."],
                ["3. Complete final quality assurance", "Check the active promotion or event, images, restaurant information, menu and ordering links, phone, directions, hours, and the customer action experience."],
                ["4. Confirm live access", "Verify the live /r/ TapToTable Page and private TapToTable Dashboard both open correctly. Test the Dashboard in a private browser window."],
                ["5. Send TapToTable Dashboard access", "Use the Activation Email button on the restaurant card. It includes the live TapToTable Page and private TapToTable Dashboard links."],
                ["6. Record completion", "Mark the lead Activated or Won, note the sent date and recipient email, then send the 2-Day Dashboard Reminder if they have not confirmed access."],
              ].map(([title, body]) => (
                <div
                  key={title}
                  className="rounded-xl border border-white/10 bg-black/20 p-4"
                >
                  <div className="text-sm font-semibold text-white">
                    {title}
                  </div>

                  <p className="mt-2 text-xs leading-relaxed text-zinc-300">
                    {body}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-5 rounded-xl border border-amber-400/30 bg-amber-400/10 p-4">
              <div className="text-sm font-semibold text-amber-300">
                Final note to record
              </div>

              <p className="mt-2 font-mono text-xs leading-relaxed text-zinc-300">
                Payment confirmed. Final QA completed. Live page and Dashboard access emailed on [DATE] to [EMAIL].
              </p>
            </div>
          </div>
        </div>
      )}
      <div className="mt-8 flex justify-center">
        <Link
          to={staffMode ? "/staff/leads" : "/admin/leads"}
          className="inline-flex items-center justify-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-5 py-2.5 text-sm font-semibold text-cyan-300 transition-colors hover:bg-cyan-500/20"
        >
          ← Back to Leads
        </Link>
      </div>

    </AdminShell>
  );
}
